const searchMetafieldQuery = `
  metafields(identifiers: [
    {key: "product_archetype", namespace: "accentuate"},
    {key: "colorway", namespace: "accentuate"},
    {key: "pdp_swatch_selector_material", namespace: "accentuate"}
  ]){
    key
    value
  }
`;

// GTM Search Tracking Function
function triggerSearchTracking(searchTerm, trigger) {
  const event = "search-initiated";
  const dataLayerObj = {
    "event": event,
    "search_term": searchTerm,
    "search_trigger": trigger // e.g., "predictive_click", "form_submit", "enter_key"
  };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(dataLayerObj);
}

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Handle Safari mobile keyboard "Done" button
    if (window.innerWidth < 768) {
      this.input.addEventListener('blur', this.onInputBlur.bind(this));

      // Add touch event to overlay to close search when tapped
      const overlay = document.querySelector('.overlay1');
      if (overlay) {
        overlay.addEventListener('click', () => {
          if (this.isOpen && window.innerWidth < 768) {
            this.close(true); // Close search and clear input
          }
        });
      }
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const searchTerm = this.getQuery();

    // On mobile Safari, prevent form submission when Done is pressed
    if (window.innerWidth < 768 && this.isOpen && searchTerm.length) {
      event.preventDefault();
      this.input.blur(); // Just hide keyboard
      return;
    }

    // Track search when form is submitted (via Enter key or search button click)
    if (searchTerm.length && !this.querySelector('[aria-selected="true"] a')) {
      triggerSearchTracking(searchTerm, 'form_submit');
    }

    if (!searchTerm.length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onInputBlur(event) {
    // Safari mobile specific handling
    if (window.innerWidth < 768 && this.isOpen) {
      // If search has content, keep results open but just hide keyboard
      if (this.getQuery().length) {
        event.preventDefault();
        // Keep search results visible
        return;
      }
    }
  }

  onFocusOut() {
    // On mobile, don't close when keyboard is dismissed
    if (window.innerWidth < 768 && this.isOpen && this.getQuery().length) {
      return; // Keep search open
    }

    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g'))?.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) {
      // Track search when selecting from predictive results
      const searchTerm = this.getQuery();
      if (searchTerm.length) {
        triggerSearchTracking(searchTerm, 'predictive_click');
      }

      selectedOption.click();
    }
  }

  async fetchProductMetafields(productIds) {
    if (!productIds.length) return {};

    const productQueries = productIds.map((id, index) => 
      `product${index}: product(id: "gid://shopify/Product/${id}") {
        id
        ${searchMetafieldQuery}
      }`
    ).join('\n      ');

    const query = `
      query GetProductMetafields {
        ${productQueries}
      }
    `;

    try {
      const response = await fetch(GRAPHQL_URL_VERSION, {
        method: 'POST',
        headers: graphqlHeadersObj,
        body: query,
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status}`);
      }

      const result = await response.json();

      // Transform the GraphQL response into a map of productId -> metafields
      const metafieldsMap = {};
      if (result.data) {
        Object.values(result.data).forEach(product => {
          if (product && product.id) {
            const productId = product.id.replace('gid://shopify/Product/', '');
            metafieldsMap[productId] = {};
            
            if (product.metafields && Array.isArray(product.metafields)) {
              product.metafields.forEach(metafield => {
                if (metafield && metafield.key && metafield.value) {
                  metafieldsMap[productId][metafield.key] = metafield.value;
                }
              });
            }
          }
        });
      }

      return metafieldsMap;
    } catch (error) {
      console.warn('Failed to fetch product metafields:', error);
      return {};
    }
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    try {
      // Use Shopify's predictive search API with multiple resource types and individual limits
      const searchUrl = `/search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection,query&resources[limit_scope]=each&resources[limit]=15&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title`;

      const response = await fetch(searchUrl, {
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        var error = new Error(response.status);
        this.close();
        throw error;
      }

      const data = await response.json();

      // Filter out products with tags containing "hide" or titles containing "test" or "hide"
      if (data.resources?.results?.products) {
        data.resources.results.products = data.resources.results.products.filter(product => {
          // Check if product title contains "test" or "hide" (case-insensitive)
          if (product.title && typeof product.title === 'string') {
            const titleLower = product.title.toLowerCase();
            if (titleLower.includes('test') || titleLower.includes('hide')) {
              return false;
            }
          }
          
          // Check if any tags contain "hide" (case-insensitive)
          if (product.tags && Array.isArray(product.tags)) {
            return !product.tags.some(tag => 
              tag && typeof tag === 'string' && tag.toLowerCase().includes('hide')
            );
          }
          
          return true;
        });
      }

      // Extract product IDs for metafields fetch
      const productIds = data.resources?.results?.products?.map(product => product.id) || [];
      
      // Fetch metafields for all products
      const metafieldsMap = await this.fetchProductMetafields(productIds);

      // Merge metafields into product data
      if (data.resources?.results?.products) {
        data.resources.results.products.forEach(product => {
          if (metafieldsMap[product.id]) {
            product.metafields = metafieldsMap[product.id];
          }
        });
      }

      // Transform Shopify API response to expected format
      const resultsMarkup = this.transformResults(data, searchTerm);
      // Save bandwidth keeping the cache in all instances synced
      this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
        predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
      });
      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      if (error?.code === 20) {
        // Code 20 means the call was aborted
        return;
      }
      this.close();
      throw error;
    }
  }

  transformResults(data, searchTerm) {
    const queries = data.resources?.results?.queries || [];
    // const collections = data.resources?.results?.collections || [];
    const products = data.resources?.results?.products || [];


    const hasSuggestions = queries.length > 0 // || collections.length > 0;
    const hasProducts = products.length > 0;

    if (!hasSuggestions && !hasProducts) {
      return `
        <div id="predictive-search-results" role="listbox">
          <div class="predictive-search__search-for-button">
            <button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">
              <span data-predictive-search-search-for-text>Search for "${searchTerm}"</span>
              <span class="svg-wrapper">→</span>
            </button>
          </div>
        </div>
        <span class="hidden" data-predictive-search-live-region-count-value>No results</span>
      `;
    }

    // Build suggestions HTML (queries and collections)
    let suggestionsHtml = '';
    if (hasSuggestions) {
      suggestionsHtml = `
        <div class="predictive-search__result-group">
          <div>
            <h2 id="predictive-search-queries" class="predictive-search__heading text-body caption-with-letter-spacing">Suggestions</h2>
            <ul id="predictive-search-results-queries-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-queries">
      `;

      // Add query suggestions
      queries.forEach((query, index) => {
        suggestionsHtml += `
          <li id="predictive-search-option-query-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
            <a href="/search?q=${encodeURIComponent(query.text)}" class="predictive-search__item link link--text" tabindex="-1">
              <div class="predictive-search__item-content predictive-search__item-content--centered">
                <p class="predictive-search__item-heading predictive-search__item-query-result h5" aria-label="${query.text}">
                  ${query.styled_text || query.text}
                </p>
              </div>
            </a>
          </li>
        `;
      });

      // Add collection suggestions
      /*
      collections.forEach((collection, index) => {
        suggestionsHtml += `
          <li id="predictive-search-option-collection-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
            <a href="${collection.url}" class="predictive-search__item link link--text" tabindex="-1">
              <div class="predictive-search__item-content predictive-search__item-content--centered">
                <p class="predictive-search__item-heading h5">${collection.title}</p>
              </div>
            </a>
          </li>
        `;
      });
      */

      suggestionsHtml += `
            </ul>
          </div>
        </div>
      `;
    }

    // Build products HTML
    let productsHtml = '';
    if (hasProducts) {
      productsHtml = `
        <div class="predictive-search__result-group">
          <div>
            <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Products</h2>
            <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
      `;

      products.forEach((product, index) => {
        const imageUrl = product.image ? product.image : '';
        productsHtml += `
          <li id="predictive-search-option-product-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
            <a href="/products/${product.handle}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
              ${imageUrl ? `<img class="predictive-search__image" src="${imageUrl}" alt="thumbnail" width="50" height="50">` : ''}
              <div class="predictive-search__item-content predictive-search__item-content--centered">
                <p class="predictive-search__item-heading h5">${product.title}</p>
              </div>
            </a>
          </li>
        `;
      });

      productsHtml += `
            </ul>
          </div>
        </div>
      `;
    }

    const wrapperClasses = hasSuggestions
      ? 'predictive-search__results-groups-wrapper'
      : 'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';

    const totalResults = queries.length + products.length;
    let countMessage = `${totalResults} result${totalResults !== 1 ? 's' : ''}`;
    if (hasSuggestions) {
      countMessage += `: ${queries.length} suggestion${(queries.length) !== 1 ? 's' : ''}`;
    }
    if (hasProducts) {
      if (hasSuggestions) countMessage += ', ';
      countMessage += `${products.length} product${products.length !== 1 ? 's' : ''}`;
    }

    return `
      <div id="predictive-search-results" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="${wrapperClasses}">
          ${suggestionsHtml}
          ${productsHtml}
        </div>
        <div class="predictive-search__search-for-button">
          <button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">
            <span data-predictive-search-search-for-text>Search for "${searchTerm}"</span>
            <span class="svg-wrapper">→</span>
          </button>
        </div>
      </div>
      <span class="hidden" data-predictive-search-live-region-count-value">${countMessage}</span>
    `;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText || 'Loading...');
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (this.statusElement) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    // Add click tracking to "Search for [term]" button (full search fallback)
    const searchForButton = this.predictiveSearchResults.querySelector('button.predictive-search__item--term');
    if (searchForButton) {
      searchForButton.addEventListener('click', () => {
        const searchTerm = this.getQuery();
        if (searchTerm.length) {
          triggerSearchTracking(searchTerm, 'full_search_button');
        }
      });
    }

    // Add click tracking to all other predictive search result links (products/suggestions)
    const resultLinks = this.predictiveSearchResults.querySelectorAll('a');
    resultLinks.forEach(link => {
      link.addEventListener('click', () => {
        const searchTerm = this.getQuery();
        if (searchTerm.length) {
          triggerSearchTracking(searchTerm, 'predictive_result_click');
        }
      });
    });

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('#header')?.getBoundingClientRect()?.bottom || 400;
    return this.resultsMaxHeight;
  }

  open() {
    if(window.innerWidth >= 768) {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    } else {
      // On mobile, make the results container scrollable and fill viewport
      this.predictiveSearchResults.style.maxHeight = `calc(78vh - ${document.querySelector('#header')?.getBoundingClientRect()?.bottom || 60}px)`;
      this.predictiveSearchResults.style.overflowY = 'auto';
      this.predictiveSearchResults.style.webkitOverflowScrolling = 'touch'; // For smooth iOS scrolling
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    // Prevent background scrolling and show overlay on mobile
    if (window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed !important';
      document.body.style.width = '100%';
      this.scrollPosition = window.pageYOffset;
      document.body.style.top = `-${this.scrollPosition}px`;

      // Show overlay1
      const overlay = document.querySelector('.overlay1');
      if (overlay) {
        overlay.style.display = 'block';
        overlay.style.background = 'rgba(0, 0, 0, 0.8)';
        overlay.style.height = '100%';
        overlay.style.left = '0';
        overlay.style.maxHeight = '100%';
        overlay.style.position = 'fixed';
        overlay.style.top = '0';
        overlay.style.width = '100%';
        overlay.style.zIndex = '9'; // Just below predictive search
      }
    }
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');

    // Restore background scrolling and hide overlay
    if (window.innerWidth < 768) {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (this.scrollPosition !== undefined) {
        window.scrollTo(0, this.scrollPosition);
      }

      // Hide overlay1
      const overlay = document.querySelector('.overlay1');
      if (overlay) {
        overlay.style.display = '';
        overlay.style.background = '';
        overlay.style.height = '';
        overlay.style.left = '';
        overlay.style.maxHeight = '';
        overlay.style.position = '';
        overlay.style.top = '';
        overlay.style.width = '';
        overlay.style.zIndex = '';
      }
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
